import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, Save, Send, ExternalLink } from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { Panel, inputCls } from "@/components/admin/cc";
import { fmtDate } from "@/lib/labels";

export const ReviewLinkSetting = () => {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings/review-link", { headers: authHeaders() }).then(({ data }) => setUrl(data.url)).catch(() => {});
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings/review-link", { url }, { headers: authHeaders() });
      setUrl(data.url);
      toast.success("Google-reviewlink opgeslagen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt.");
    } finally { setBusy(false); }
  };

  return (
    <Panel className="p-6" data-testid="review-link-setting">
      <div className="flex items-center gap-2 mb-2">
        <Star className="w-5 h-5 text-amber-400" />
        <h2 className="font-heading font-bold text-lg text-cc-ink">Google-reviewlink</h2>
      </div>
      <p className="font-body text-xs text-cc-faint mb-4">Google Bedrijfsprofiel → "Vraag om reviews" → link kopiëren. Wordt gebruikt in de reviewmail naar klanten met een afgerond project.</p>
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://g.page/r/…/review" data-testid="review-link-input" className={`${inputCls} mb-3`} />
      <div className="flex gap-2">
        <button onClick={save} disabled={busy} data-testid="review-link-save" className="inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-4 py-2 font-body text-sm font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Opslaan
        </button>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" data-testid="review-link-open" className="inline-flex items-center gap-2 rounded-xl border border-cc-border px-4 py-2 font-body text-sm font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors">
            <ExternalLink className="w-4 h-4" /> Testen
          </a>
        )}
      </div>
    </Panel>
  );
};

export const ReviewRequest = ({ lead, onSent }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/review-request`, {}, { headers: authHeaders() });
      toast.success("Reviewmail verstuurd.");
      onSent(data.review_requested_at);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Versturen mislukt.");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 flex flex-wrap items-center gap-3 rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-3" data-testid="review-request">
      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
      <p className="font-body text-sm text-cc-sub flex-1 min-w-[200px]">
        {lead.review_requested_at ? `Review gevraagd op ${fmtDate(lead.review_requested_at)}.` : "Project afgerond — vraag de klant om een Google-review."}
      </p>
      <button onClick={send} disabled={busy} data-testid="review-request-send" className="inline-flex items-center gap-2 rounded-xl bg-amber-400 text-stone-900 px-4 py-2 font-body text-sm font-semibold hover:bg-amber-300 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {lead.review_requested_at ? "Opnieuw vragen" : "Review vragen"}
      </button>
    </div>
  );
};
